import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
} from '@mui/material';
import { QueryStats, AccessTime, Forum, Person } from '@mui/icons-material';
import correspondenceService from '../services/correspondenceService';

const StatBox = ({ icon, label, value }) => (
  <Paper sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
    <Box sx={{ color: 'primary.main' }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" fontWeight={700}>{value}</Typography>
    </Box>
  </Paper>
);

const formatDuration = (hours) => {
  if (hours == null) return '—';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} días`;
};

const CorrespondenceMetrics = ({ correspondenceId }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!correspondenceId) return;
    setLoading(true);
    correspondenceService.getThreadMetrics(correspondenceId)
      .then((data) => setMetrics(data.data || data))
      .catch(() => setError('Error al cargar las métricas'))
      .finally(() => setLoading(false));
  }, [correspondenceId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!metrics) return null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <QueryStats color="primary" />
        <Typography variant="h5">Métricas de Tramitación</Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <StatBox
            icon={<AccessTime />}
            label="Primera respuesta"
            value={formatDuration(metrics.timeToFirstThread)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatBox
            icon={<AccessTime />}
            label="Tiempo total tramite"
            value={formatDuration(metrics.totalProcessingTime)}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatBox
            icon={<Forum />}
            label="Total hilos"
            value={metrics.totalThreads ?? 0}
          />
        </Grid>
      </Grid>

      {metrics.byUser && metrics.byUser.length > 0 && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Participación por Usuario
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Rol</TableCell>
                  <TableCell align="center">Hilos</TableCell>
                  <TableCell align="center">Tiempo promedio de respuesta</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {metrics.byUser.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person fontSize="small" color="action" />
                        <Typography variant="body2">{row.userName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.roleName ? (
                        <Chip label={row.roleName} size="small" variant="outlined" />
                      ) : '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={row.threadCount} size="small" color="primary" />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {formatDuration(row.avgResponseTimeHours)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default CorrespondenceMetrics;
